import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { bindPolePlanGeometry, getBoundLocationPolygons } from "../../helpers/PolePlanGeometryHelper";
import { BoundPlanPolygon, BoundPolePlan } from "../../models/PolePlanGeometry";
import {
    OverviewScope,
    PoleViewModel,
    ServiceScoreViewModel,
    VALUE_CODES,
    ValueCode
} from "../../models/OverviewDashboard";
import LocationService, { MapLocation } from "../../services/LocationService";
import { registerLeafletIndoor } from "../../utils/registerLeafletIndoor";
import CompactValueStats, { CompactValueSet } from "./CompactValueStats";
import styles from "./PoleServicesMapWidget.module.css";

interface PoleServicesMapWidgetProps {
    fhirBaseUrl: string;
    pole: PoleViewModel;
    services: ServiceScoreViewModel[];
    scope: OverviewScope;
    selectedServiceId?: string;
    selectedValue: ValueCode;
    onScopeChange: (scope: OverviewScope) => void;
    onServiceSelect: (serviceId: string) => void;
}

type IndoorFeatureProperties = {
    level: string;
    kind: "structure" | "location";
    serviceId?: string;
    serviceName?: string;
    locationName?: string;
    score?: number;
    summaryAnchor: boolean;
};

type IndoorFeature = GeoJSON.Feature<GeoJSON.Polygon, IndoorFeatureProperties>;

type OverlayPosition = {
    left: number;
    top: number;
};

const PoleServicesMapWidget: FunctionComponent<PoleServicesMapWidgetProps> = ({
    fhirBaseUrl,
    pole,
    services,
    scope,
    selectedServiceId,
    selectedValue,
    onScopeChange,
    onServiceSelect
}) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map>();
    const indoorLayerRef = useRef<any>();
    const levelControlRef = useRef<any>();
    const [locations, setLocations] = useState<MapLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [overlayPosition, setOverlayPosition] = useState<OverlayPosition>();

    const locationService = useMemo(
        () => new LocationService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );

    const selectedService = useMemo(
        () => selectedServiceId
            ? services.find((item) => item.service.id === selectedServiceId)
            : undefined,
        [selectedServiceId, services]
    );

    const poleSummaryValues = useMemo(
        () => buildPoleSummaryValues(services),
        [services]
    );

    const summaryValues: CompactValueSet | undefined = scope === "pole"
        ? poleSummaryValues
        : selectedService?.values;
    const summaryTitle = scope === "pole"
        ? "MOY. PÔLE"
        : selectedService?.service.name;

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(undefined);

        locationService
            .searchByManagingOrganizations(
                services.map((item) => item.service.reference)
            )
            .then((result) => {
                if (mounted) setLocations(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger les lieux des services.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [locationService, services]);

    const boundPlan = useMemo<BoundPolePlan>(
        () => bindPolePlanGeometry(
            services.map((item) => item.service),
            locations
        ),
        [services, locations]
    );

    const boundLocationCount = useMemo(
        () => getBoundLocationPolygons(boundPlan).length,
        [boundPlan]
    );

    const featureCollection = useMemo<GeoJSON.FeatureCollection<GeoJSON.Polygon, IndoorFeatureProperties>>(
        () => buildIndoorFeatures(services, boundPlan, selectedValue),
        [services, boundPlan, selectedValue]
    );

    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        registerLeafletIndoor(L);
        mapRef.current = L.map(mapContainerRef.current, {
            crs: L.CRS.Simple,
            minZoom: -2,
            maxZoom: 3,
            zoomControl: true,
            attributionControl: false
        });

        return () => {
            mapRef.current?.remove();
            mapRef.current = undefined;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const leafletWithIndoor = L as typeof L & {
            Indoor?: new (data: GeoJSON.GeoJsonObject, options?: unknown) => any;
            Control: typeof L.Control & {
                Level?: new (options: { level: string; levels: string[] }) => any;
            };
        };

        if (!map || !leafletWithIndoor.Indoor || featureCollection.features.length === 0) return;

        if (indoorLayerRef.current) {
            map.removeLayer(indoorLayerRef.current);
            indoorLayerRef.current = undefined;
        }
        if (levelControlRef.current) {
            map.removeControl(levelControlRef.current);
            levelControlRef.current = undefined;
        }

        const indoorLayer = new leafletWithIndoor.Indoor(featureCollection, {
            getLevel: (feature: IndoorFeature) => feature.properties.level,
            style: (feature: IndoorFeature) => getFeatureStyle(
                feature,
                scope,
                selectedServiceId
            ),
            onEachFeature: (feature: IndoorFeature, layer: L.Layer) => {
                const properties = feature.properties;
                if (
                    properties.kind !== "location" ||
                    !properties.serviceId ||
                    !properties.serviceName ||
                    !properties.locationName
                ) {
                    return;
                }

                const score = properties.score === undefined
                    ? "Donnée indisponible"
                    : `${properties.score.toFixed(1)} / 10`;

                layer.bindPopup(
                    `<strong>${escapeHtml(properties.serviceName)}</strong><br/>` +
                    `${escapeHtml(properties.locationName)}<br/>Score : ${score}`
                );
                layer.on("click", () => onServiceSelect(properties.serviceId as string));
            },
            markerForFeature: (feature: IndoorFeature) => {
                if (
                    feature.properties.kind !== "location" ||
                    !feature.properties.summaryAnchor ||
                    !feature.properties.serviceId ||
                    !feature.properties.serviceName
                ) {
                    return undefined;
                }

                if (
                    scope === "services" &&
                    feature.properties.serviceId === selectedServiceId
                ) {
                    return undefined;
                }

                const serviceFeatures = featureCollection.features.filter((candidate) =>
                    candidate.properties.kind === "location" &&
                    candidate.properties.serviceId === feature.properties.serviceId &&
                    candidate.properties.level === feature.properties.level
                );
                const serviceBounds = L.geoJSON(
                    serviceFeatures as unknown as GeoJSON.GeoJsonObject[]
                ).getBounds();

                return L.marker(serviceBounds.getCenter(), {
                    interactive: false,
                    icon: L.divIcon({
                        className: styles.mapLabel,
                        html: `<span>${escapeHtml(feature.properties.serviceName)}</span>`,
                        iconSize: undefined
                    })
                });
            }
        });

        const levels = indoorLayer.getLevels() as string[];
        const initialLevel = levels[0] ?? "0";
        indoorLayer.setLevel(initialLevel);
        indoorLayer.addTo(map);
        indoorLayerRef.current = indoorLayer;

        if (leafletWithIndoor.Control.Level && levels.length > 1) {
            const levelControl = new leafletWithIndoor.Control.Level({ level: initialLevel, levels });
            levelControl.addEventListener("levelchange", indoorLayer.setLevel, indoorLayer);
            levelControl.addTo(map);
            levelControlRef.current = levelControl;
        }

        const bounds = L.geoJSON(featureCollection).getBounds();
        if (bounds.isValid()) map.fitBounds(bounds.pad(0.05));
        window.setTimeout(() => map.invalidateSize(), 0);
    }, [featureCollection, onServiceSelect, scope, selectedServiceId]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || featureCollection.features.length === 0) {
            setOverlayPosition(undefined);
            return;
        }

        const boundFeatures = featureCollection.features.filter(
            (feature) => feature.properties.kind === "location"
        );
        const evaluatedFeatures = scope === "services"
            ? boundFeatures.filter(
                (feature) => feature.properties.serviceId === selectedServiceId
            )
            : boundFeatures;

        if (evaluatedFeatures.length === 0) {
            setOverlayPosition(undefined);
            return;
        }

        const evaluatedBounds = L.geoJSON(
            evaluatedFeatures as unknown as GeoJSON.GeoJsonObject[]
        ).getBounds();

        if (!evaluatedBounds.isValid()) {
            setOverlayPosition(undefined);
            return;
        }

        const updatePosition = () => {
            const point = map.latLngToContainerPoint(evaluatedBounds.getCenter());
            setOverlayPosition({ left: point.x, top: point.y });
        };

        updatePosition();
        const timeoutId = window.setTimeout(updatePosition, 0);
        map.on("zoom move resize", updatePosition);

        return () => {
            window.clearTimeout(timeoutId);
            map.off("zoom move resize", updatePosition);
        };
    }, [featureCollection, scope, selectedServiceId]);

    return (
        <section className={styles.card}>
            <header className={styles.header}>
                <div className={styles.tabs} role="tablist" aria-label="Périmètre des statistiques">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "pole"}
                        className={scope === "pole" ? styles.activeTab : styles.tab}
                        onClick={() => onScopeChange("pole")}
                    >
                        Pôle
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={scope === "services"}
                        className={scope === "services" ? styles.activeTab : styles.tab}
                        onClick={() => onScopeChange("services")}
                    >
                        Services
                    </button>
                </div>
                <span className={styles.poleName}>{pole.name}</span>
            </header>

            <div className={styles.mapShell}>
                {loading && <div className={styles.state}>Chargement de la cartographie…</div>}
                {error && <div className={styles.error}>{error}</div>}
                {!loading && !error && boundLocationCount === 0 && (
                    <div className={styles.state}>Aucun lieu actif n'est rattaché aux services du pôle.</div>
                )}
                <div ref={mapContainerRef} className={styles.map} aria-label={`Cartographie du pôle ${pole.name}`} />
                {!loading && !error && overlayPosition && summaryValues && summaryTitle && (
                    <div
                        className={styles.statsOverlay}
                        style={{ left: overlayPosition.left, top: overlayPosition.top }}
                    >
                        <CompactValueStats title={summaryTitle} values={summaryValues} />
                    </div>
                )}
            </div>
        </section>
    );
};

function buildIndoorFeatures(
    services: ServiceScoreViewModel[],
    plan: BoundPolePlan,
    selectedValue: ValueCode
): GeoJSON.FeatureCollection<GeoJSON.Polygon, IndoorFeatureProperties> {
    const serviceScores = new Map(
        services.map((service) => [
            service.service.id,
            service.values[selectedValue].score
        ])
    );
    const anchorKeys = new Set<string>();
    const features = plan.polygons.map((polygon) => {
        const anchorKey = polygon.serviceId
            ? `${polygon.level}|${polygon.serviceId}`
            : undefined;
        const summaryAnchor = !!polygon.isBoundLocation && !!anchorKey && !anchorKeys.has(anchorKey);

        if (summaryAnchor && anchorKey) anchorKeys.add(anchorKey);

        return createFeature(
            polygon,
            polygon.serviceId ? serviceScores.get(polygon.serviceId) : undefined,
            summaryAnchor
        );
    });

    return { type: "FeatureCollection", features };
}

function createFeature(
    polygon: BoundPlanPolygon,
    score: number | undefined,
    summaryAnchor: boolean
): IndoorFeature {
    const coordinates = polygon.polygon.map(([x, y]) => [x, y]);
    const firstPoint = coordinates[0];
    const lastPoint = coordinates[coordinates.length - 1];

    if (
        firstPoint &&
        lastPoint &&
        (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1])
    ) {
        coordinates.push([firstPoint[0], firstPoint[1]]);
    }

    return {
        type: "Feature",
        properties: {
            level: polygon.level,
            kind: polygon.isBoundLocation ? "location" : "structure",
            serviceId: polygon.serviceId,
            serviceName: polygon.serviceName,
            locationName: polygon.locationName,
            score,
            summaryAnchor
        },
        geometry: {
            type: "Polygon",
            coordinates: [coordinates]
        }
    };
}

function getFeatureStyle(
    feature: IndoorFeature,
    scope: OverviewScope,
    selectedServiceId?: string
): L.PathOptions {
    if (feature.properties.kind === "structure") {
        return {
            color: "#aebbc4",
            weight: 1.2,
            fillColor: "#f7f9fa",
            fillOpacity: 0.94
        };
    }

    if (scope === "services") {
        const selected = feature.properties.serviceId === selectedServiceId;

        return {
            color: selected ? "#155a70" : "#c8d0d5",
            weight: selected ? 4 : 1.5,
            fillColor: selected ? "#d9eef8" : "#dfe4e7",
            fillOpacity: selected ? 0.92 : 0.62
        };
    }

    return {
        color: "#c4d8ea",
        weight: 2,
        fillColor: scoreToColor(feature.properties.score),
        fillOpacity: 0.72
    };
}

function buildPoleSummaryValues(services: ServiceScoreViewModel[]): CompactValueSet {
    return VALUE_CODES.reduce((result, code) => {
        const scores = services
            .map((service) => service.values[code].score)
            .filter((score): score is number => score !== undefined && Number.isFinite(score));
        const score = scores.length > 0
            ? scores.reduce((sum, value) => sum + value, 0) / scores.length
            : undefined;

        result[code] = {
            score,
            percentage: score === undefined ? undefined : Math.min(Math.max(score * 10, 0), 100)
        };
        return result;
    }, {} as CompactValueSet);
}

function scoreToColor(score?: number): string {
    if (score === undefined || !Number.isFinite(score)) return "#dce9f6";

    const ratio = Math.min(Math.max(score, 0), 10) / 10;
    const red = Math.round(229 + ratio * (96 - 229));
    const green = Math.round(119 + ratio * (184 - 119));
    const blue = Math.round(118 + ratio * (138 - 118));
    return `rgb(${red}, ${green}, ${blue})`;
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

export default PoleServicesMapWidget;
